// isLoggedIn.js
//
// ⭐ WHAT "LOGGED IN" MEANS, and why the length check was not enough.
//
// This used to be `token !== null && token.length >= 128`. An EXPIRED JWT satisfies both — it is
// still a long three-part string — so the router treated a dead session as a live one. The app
// rendered its shell, `App.jsx` fired `getUserData` with the dead token, and the first thing to
// notice was an API call coming back `401 Please authenticate. jwt expired`. The operator saw
// himself logged in, clicked once, and was ejected to the login screen with a console full of 401s.
//
// A page reload could not fix it: the dead token was still in storage, still ≥128 characters, and
// the same sequence ran again.
//
// So the gate now reads the token's OWN expiry. The client only DECODES here — verification is the
// server's job and its secret never leaves it — which is exactly why this is safe to do in the
// browser and why a forged token gains nothing: it still has to survive `jwt.verify` on every call.
//
// ⚠️ An expired token is also REMOVED, not merely rejected. Leaving it in storage means every
// subsequent read has to re-derive the same conclusion, and any code that checks presence rather
// than validity — there is still some — keeps believing there is a session.

import { useEffect } from 'react';
import { endSession } from './utils/sessionBridge';
import { recordClientAuthEvent } from './apiTools/apiClient';

/** Decode a JWT payload without verifying it. Returns null for anything that is not a JWT. */
function readPayload(token) {
  try {
    const part = String(token).split('.')[1];
    if (!part) return null;
    // base64url -> base64, and restore the padding `atob` requires.
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    // Malformed, truncated, or not base64 at all. Not an error worth throwing: the answer to
    // "is this a session?" is simply no.
    return null;
  }
}

/**
 * ⭐ WAS THE LAST REFUSAL AN EXPIRY, OR WAS NOBODY EVER SIGNED IN? (2026-10-26)
 *
 * The router has to tell those apart and cannot: by the time it asks, the dead token has been
 * removed, so an expired session and a guest look identical — both are simply "no token".
 * ProtectedRoute needs the difference, because an expiry should end in a real page load (the
 * store cleared, the password manager offered a form it believes is new) while a guest following
 * a protected link should just be routed.
 *
 * ⓘ Module state, deliberately. It must survive a router navigation — same JS context — and
 * must NOT survive a document load, or the hard redirect would see its own marker and loop.
 */
let expiredAtMs = 0;

export function sessionJustExpired(withinMs = 60 * 1000) {
  return expiredAtMs !== 0 && Date.now() - expiredAtMs < withinMs;
}

/** Test seam. Nothing in the app calls this. */
export function __resetExpiryMarker() {
  expiredAtMs = 0;
}

/** Seconds of tolerance for clock drift between this browser and the server. */
const SKEW_SECONDS = 30;

export function isTokenLive(token, nowMs = Date.now()) {
  if (!token) return false;
  // A logout once wrote the STRING "null" (localStorage.setItem stringifies), and "null" is truthy.
  if (token === 'null' || token === 'undefined') return false;
  if (token.length < 128) return false;

  const payload = readPayload(token);
  if (!payload) return false;
  // ⚠️ No `exp` claim means nothing can say the session ended, which is not the same as it being
  // live. Treated as dead: every token this app issues carries one (`expiresIn: '12h'`), so a token
  // without it is malformed or from somewhere else.
  if (typeof payload.exp !== 'number') return false;

  return payload.exp * 1000 > nowMs - SKEW_SECONDS * 1000;
}

/**
 * ⭐⭐ AND THE STORE GOES WITH IT (2026-10-24). Removing the token used to be the whole cleanup,
 * which left the app with TWO answers to "are we signed in": a token that had been deleted, and a
 * Redux `user` slice that had not. A component gating on `currentUser` then fired an authenticated
 * request with no credentials, the server said `no-credentials`, and the diagnostic panel reported
 * a session bug for what was an ordinary 12-hour expiry. Found in production by the operator:
 * `/architecture/litgroups` at 06:06 and 06:18 UTC, both with `sentAuthHeader: false`.
 *
 * ⚠️ THE RESET RUNS IN AN EFFECT, NOT IN RENDER. Dispatching while rendering is what produces
 * "Cannot update a component while rendering a different component", and in a hook this many
 * components call it would fire during half the tree. The ANSWER is still immediate — false is
 * returned on the spot, so routing does not wait for the effect.
 *
 * ⓘ The token removal stays synchronous for the same reason it always was: every later read
 * should be cheap, and any code that checks presence rather than validity must stop believing.
 */
export default function useIsLoggedIn() {
  const token = localStorage.getItem('authToken');
  const live = isTokenLive(token);

  if (!live && token !== null) {
    // ⚠️ THE MARKER IS SET IN RENDER, not in the effect, because ProtectedRoute asks for it in
    // the SAME render pass that this hook answered false in. An effect would run after the
    // routing decision had already been made, and the expiry would be mistaken for a guest.
    expiredAtMs = Date.now();
    try { localStorage.removeItem('authToken'); } catch (_) { /* storage unavailable */ }
  }

  useEffect(() => {
    if (!live && token !== null) {
      // ⭐ The crumb takes the token VALUE: storage was emptied in render above, so reading it
      // here would describe an absence rather than the credential that actually died.
      recordClientAuthEvent('token-not-live', token);
      endSession('token-not-live');
    }
  }, [live, token]);

  return live ? token : false;
}
